import React, { useState, useEffect } from 'react';
import { clientService } from '../services/api';
import { RequestStat, QuotaInfo, StatsSummary } from '../types';
import StatsChart from './charts/StatsChart';

interface StatsDashboardProps {
  clientId?: string;
  className?: string;
}

const StatsDashboard: React.FC<StatsDashboardProps> = ({ clientId, className = '' }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<RequestStat[]>([]);
  const [summary, setSummary] = useState<StatsSummary | null>(null);
  const [quota, setQuota] = useState<QuotaInfo | null>(null);
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'year'>('day');
  const [chartType, setChartType] = useState<'line' | 'bar'>('line');

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        
        if (clientId) {
          const result = await clientService.getClientStats(clientId, period);
          setStats(result.stats);
          setSummary(result.summary);
          setQuota(result.quota);
        } else {
          // Si pas de clientId, on récupère un résumé global
          const result = await clientService.getStatsOverview();
          setQuota(result.quota);
          // Dans ce cas, nous n'avons pas de stats détaillées
          setStats([]);
          setSummary(null);
        }
        
        setError(null);
      } catch (err) {
        console.error('Erreur lors du chargement des statistiques:', err);
        setError('Impossible de charger les statistiques');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [clientId, period]);

  const getQuotaColor = (percentage: number): string => {
    if (percentage < 50) return '#4CAF50'; // Vert
    if (percentage < 75) return '#FFC107'; // Jaune
    if (percentage < 90) return '#FF9800'; // Orange
    return '#F44336'; // Rouge
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return <div className="stats-loading">Chargement des statistiques...</div>;
  }

  if (error) {
    return <div className="stats-error">{error}</div>;
  }

  return (
    <div className={`stats-dashboard ${className}`}>
      <div className="stats-header">
        <h2>Statistiques d'utilisation</h2>
        
        <div className="stats-controls">
          <div className="period-selector">
            <label>Période: </label>
            <select 
              value={period} 
              onChange={(e) => setPeriod(e.target.value as 'day' | 'week' | 'month' | 'year')}
              className="period-select"
            >
              <option value="day">Aujourd'hui</option>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="year">Cette année</option>
            </select>
          </div>
          
          <div className="chart-type-selector">
            <label>Type de graphique: </label>
            <div className="chart-type-buttons">
              <button 
                className={`chart-type-btn ${chartType === 'line' ? 'active' : ''}`}
                onClick={() => setChartType('line')}
              >
                Ligne
              </button>
              <button 
                className={`chart-type-btn ${chartType === 'bar' ? 'active' : ''}`}
                onClick={() => setChartType('bar')}
              >
                Barre
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Affichage du quota */}
      {quota && (
        <div className="quota-section">
          <h3>Quota d'utilisation</h3>
          <div className="quota-info">
            <div className="quota-progress">
              <div className="quota-bar">
                <div 
                  className="quota-fill" 
                  style={{ 
                    width: `${Math.min(parseFloat(quota.percentage), 100)}%`,
                    backgroundColor: getQuotaColor(parseFloat(quota.percentage))
                  }}
                />
              </div>
              <div className="quota-text">
                {quota.used} / {quota.total} requêtes ({quota.percentage}%)
              </div>
            </div>
            {quota.plan && (
              <div className="plan-info">
                <span className="plan-label">Plan:</span> 
                <span className="plan-name">{quota.plan}</span>
                {quota.rateLimit && (
                  <span className="rate-limit"> - Limite: {quota.rateLimit} req/min</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Résumé des statistiques */}
      {summary && (
        <div className="stats-summary">
          <div className="summary-card">
            <div className="summary-value">{summary.totalRequests}</div>
            <div className="summary-label">Requêtes totales</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{formatBytes(summary.totalBandwidth)}</div>
            <div className="summary-label">Bande passante</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{summary.avgResponseTime.toFixed(2)} ms</div>
            <div className="summary-label">Temps de réponse moyen</div>
          </div>
          <div className="summary-card">
            <div className="summary-value">{summary.errorRate.toFixed(1)}%</div>
            <div className="summary-label">Taux d'erreur</div>
          </div>
        </div>
      )}
      
      {/* Graphiques */}
      {stats && stats.length > 0 && (
        <div className="stats-charts">
          <div className="chart-container">
            <StatsChart 
              stats={stats} 
              type={chartType} 
              dataType="requests" 
              title="Nombre de requêtes"
              height={300}
            />
          </div>
          <div className="chart-container">
            <StatsChart 
              stats={stats} 
              type={chartType} 
              dataType="bandwidth" 
              title="Bande passante utilisée"
              height={300}
            />
          </div>
        </div>
      )}
      
      <style>
        {`
          .stats-dashboard {
            padding: 20px;
            background-color: #f8f9fa;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
          }
          
          .stats-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            flex-wrap: wrap;
          }
          
          .stats-header h2 {
            font-size: 1.5rem;
            font-weight: 600;
            color: #333;
            margin: 0;
          }
          
          .stats-controls {
            display: flex;
            gap: 20px;
            align-items: center;
            flex-wrap: wrap;
          }
          
          .period-selector, .chart-type-selector {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          
          .period-select {
            padding: 6px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            background-color: white;
          }
          
          .chart-type-buttons {
            display: flex;
            border: 1px solid #ddd;
            border-radius: 4px;
            overflow: hidden;
          }
          
          .chart-type-btn {
            padding: 6px 12px;
            background-color: white;
            border: none;
            cursor: pointer;
            transition: background-color 0.2s;
          }
          
          .chart-type-btn.active {
            background-color: #e9ecef;
            font-weight: 500;
          }
          
          .quota-section {
            margin-bottom: 24px;
            background-color: white;
            padding: 16px;
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          
          .quota-section h3 {
            font-size: 1.1rem;
            font-weight: 500;
            margin-top: 0;
            margin-bottom: 12px;
            color: #333;
          }
          
          .quota-bar {
            height: 16px;
            background-color: #e9ecef;
            border-radius: 8px;
            overflow: hidden;
            margin-bottom: 8px;
          }
          
          .quota-fill {
            height: 100%;
            transition: width 0.3s ease;
          }
          
          .quota-text {
            font-size: 0.9rem;
            color: #666;
          }
          
          .plan-info {
            margin-top: 8px;
            font-size: 0.9rem;
          }
          
          .plan-label {
            color: #666;
          }
          
          .plan-name {
            font-weight: 600;
            text-transform: capitalize;
          }
          
          .stats-summary {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 16px;
            margin-bottom: 24px;
          }
          
          .summary-card {
            background-color: white;
            padding: 16px;
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            text-align: center;
          }
          
          .summary-value {
            font-size: 1.8rem;
            font-weight: 600;
            color: #333;
            margin-bottom: 8px;
          }
          
          .summary-label {
            font-size: 0.9rem;
            color: #666;
          }
          
          .stats-charts {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
            gap: 20px;
          }
          
          .chart-container {
            background-color: white;
            padding: 16px;
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          
          .stats-loading, .stats-error {
            padding: 40px;
            text-align: center;
            background-color: white;
            border-radius: 8px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          
          .stats-error {
            color: #dc3545;
          }
          
          @media (max-width: 768px) {
            .stats-header {
              flex-direction: column;
              align-items: flex-start;
              gap: 16px;
            }
            
            .stats-controls {
              width: 100%;
            }
            
            .stats-charts {
              grid-template-columns: 1fr;
            }
          }
        `}
      </style>
    </div>
  );
};

export default StatsDashboard;
